import { StyleObject } from 'styletron-react'
import { merchantTheme } from 'theme'

/**
 * Chrome for the mint "AI reasoning" card.
 *
 * Two components render this card at the SAME spot in the engine's right column, one
 * swapping for the other as the wizard leaves its guided phase: the scripted
 * `ReasoningChecklist` and `DecisionLogPanel`'s wizard branch. They used to carry
 * duplicate copies of these rules (`ReasoningChecklist.tsx` even said so in a comment),
 * which means any drift between them shows up to the merchant as the card visibly
 * changing at the phase flip. One source instead.
 *
 * Values measured off the Figma export — see
 * `.context/plans/plan_20260817_rai-wizard-figma-parity.md` §2.1.
 */

// The wash is a named local constant rather than a theme token because NEITHER
// spearmint10 is this colour: `merchantTheme`'s is #E3F7F7 and `palette`'s is #E7F9F9,
// and both read visibly darker and greener than the design. DecisionLogPanel already
// documented #F2FAFA as the target and knowingly approximated it with spearmint10; the
// measurement (240,250,250) confirms that approximation was the visible error.
export const REASONING_CARD_WASH = '#F2FAFA'
// 2px, not 3px — and spearmint50, not spearmint30. The shipped rule was both too wide
// and too pale; the Figma rule samples at (55,185,182), which is spearmint50 territory.
export const REASONING_RULE_WIDTH_PX = 2
export const REASONING_CARD_PADDING_PX = 12
export const REASONING_CARD_RADIUS_PX = 4

// Step rhythm. Both the card title and the steps render as Text.BodyText5 (12px) — the
// Figma separates a bold step title from its prose by WEIGHT alone, where the shipped
// card used BodyText4/20px. The size drops; the colour stays grey90, because grey70 (the
// colour the Figma samples to) is 4.49:1 on this wash and 12px needs 4.5:1. uikit's
// typography tokens carry no line-height, so the 16px rhythm is stated here.
export const REASONING_LINE_HEIGHT_PX = 16
export const REASONING_TITLE_MARGIN_PX = 14
/**
 * The card's font size, in px. Everything inside it — title, step title, step prose AND
 * the "Tweak …" links — renders at this one size; BodyText5 supplies it for the first
 * three, and this constant is for the link, whose size has to be set inside a baseui
 * `overrides` style object where a Text variant cannot reach.
 *
 * It is a shared constant rather than a literal because the two DID drift: dropping the
 * link's explicit `fontSize` left baseui's button default in charge and the links
 * rendered at ~15px beside 12px prose, which is what shipped in the first pass.
 */
export const REASONING_FONT_SIZE_PX = 12
// 8px of CSS gap resolves to the Figma's 13px ink-to-ink, once the ~2.5px of line
// leading above and below each step's text is accounted for.
export const REASONING_STEP_GAP_PX = 8
export const REASONING_TWEAK_LINE_HEIGHT_PX = 18
export const REASONING_BULLET_SIZE_PX = 13

/**
 * The "Tweak …" link's `overrides.BaseButton.style`.
 *
 * A builder for the same reason the card is one — and specifically so the two
 * load-bearing type rules are assertable. They were not while this lived inline in
 * ReasoningChecklist's JSX: the suite could only compare REASONING_FONT_SIZE_PX to 12,
 * which is a constant against a literal and stays green when the override stops using it,
 * i.e. exactly the regression the comment claimed to lock.
 *
 *   context: uikit shallow-spreads a caller override OVER its own kind-derived BaseButton
 *   entry, so a PARTIAL block silently discards the minimal styling and yields baseui's
 *   filled default. This has to stay complete, transparent background included
 *   (SimulatorEngine.tsx:39-58).
 */
export const buildTweakLinkStyle = (linkColor: string): StyleObject => ({
  backgroundColor: 'transparent',
  color: linkColor,
  // BOTH pinned, and both load-bearing: without the font size baseui's button default
  // takes over and the link renders ~15px beside 12px prose, and without the line-height
  // baseui reintroduces the blank line the 2px wrapper margin exists to avoid.
  fontSize: `${REASONING_FONT_SIZE_PX}px`,
  lineHeight: `${REASONING_TWEAK_LINE_HEIGHT_PX}px`,
  paddingTop: '0',
  paddingBottom: '0',
  paddingLeft: '0',
  paddingRight: '0',
  ':hover:enabled': {
    backgroundColor: 'transparent',
    color: linkColor,
  },
  ':active:enabled': {
    backgroundColor: 'transparent',
  },
})

// Exported as a builder so the geometry is unit-testable: styletron's css() is a no-op
// in the jsdom test env, so these rules can never be read back off the rendered DOM.
export const buildReasoningCardStyle = (): StyleObject => ({
  backgroundColor: REASONING_CARD_WASH,
  borderLeftWidth: `${REASONING_RULE_WIDTH_PX}px`,
  borderLeftStyle: 'solid',
  borderLeftColor: merchantTheme.colors.spearmint50,
  borderRadius: `${REASONING_CARD_RADIUS_PX}px`,
  padding: `${REASONING_CARD_PADDING_PX}px`,
  // Deliberately NO flexGrow (D5, approved 2026-08-17): the card hugs its content like
  // the Figma rather than stretching to the phone's bottom. The right column keeps its
  // height, so what a tall card used to fill is now intentional whitespace.
})
